import React, { useState } from 'react';

import classNames from 'classnames';
import { format, parseISO } from 'date-fns';

import ProjectDashboardEmptyState from './ProjectDashboardEmptyState';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import SegmentedControl from 'CommonComponents/SegmentedControl';

// Number of milestones shown before the "Show all" toggle expands the list.
const TOP_N = 10;

// The forward windows the card offers, mirroring ProjectService::KEY_MILESTONE_WINDOW_OPTIONS
// — the controller rejects anything else, so the two lists have to agree.
export const DEFAULT_WINDOW_DAYS = 90;
const WINDOW_OPTIONS = [30, 60, 90, 180];

// RAG treatment, keyed on the backend's ProjectMilestoneRagStatus values. Late and
// on-track reuse the red/green pill fills already in the palette; at risk uses the
// amber pair the rest of the app uses for warnings. Full literal class strings so
// Tailwind's content scanner keeps them in the build.
const RAG_STATUSES = {
    on_track: { label: 'On track', classes: 'tw-bg-green-25 tw-text-green-700' },
    at_risk: { label: 'At risk', classes: 'tw-bg-amber-100 tw-text-amber-800' },
    late: { label: 'Late', classes: 'tw-bg-red-25 tw-text-red-700' },
};

// Numeric rather than the wireframe's "28 Feb 2027": date-fns has no locale wiring
// anywhere in this project, so an `MMM` month would resolve against en-US and be
// unreachable to a translator editing ar.json — untranslatable rather than merely
// untranslated. Numeric dates are locale-neutral and match the Outlook table further
// down this same page. Signed off as a deliberate divergence from the wireframe.
//
// Dates arrive as plain 'YYYY-MM-DD' strings, so parseISO gives local midnight and
// cannot shift the day across a timezone.
const formatDue = (date) => (date ? format(parseISO(date), 'dd-MM-yyyy') : '-');

function RagPill({ status }) {
    const rag = RAG_STATUSES[status];

    if (!rag) {
        return <span className="tw-text-charcoal-50">—</span>;
    }

    return (
        <span className={classNames('tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium', rag.classes)}>
            {$t(rag.label)}
        </span>
    );
}

function MilestoneRow({ row, onOpen }) {
    return (
        <tr className="tw-border-t tw-border-grey">
            <td className="tw-cursor-pointer tw-py-3 tw-pe-4 tw-ps-3" onClick={() => onOpen(row.id)}>
                <span className="tw-text-sm tw-text-charcoal">{row.description}</span>
                {/* Key milestones only. On the "key milestones" view that is every row, as
                    the wireframe draws it; on the "all milestones" view it is what tells a
                    key milestone apart from an ordinary one. */}
                {row.is_key_milestone && (
                    <span className="tw-ms-1.5 tw-text-amber-500">
                        <span aria-hidden="true">★</span>
                        <span className="tw-sr-only">{$t('Key milestone')}</span>
                    </span>
                )}
            </td>
            <td className="tw-py-3 tw-pe-4 tw-text-sm tw-text-charcoal">{row.functional_area || '—'}</td>
            <td className="tw-py-3 tw-pe-4 tw-text-sm tw-text-charcoal">{formatDue(row.target_completion_date)}</td>
            <td className="tw-py-3">
                <RagPill status={row.rag_status} />
            </td>
        </tr>
    );
}

function MilestoneScopeToggle({ keyMilestonesOnly, onChange }) {
    return (
        <SegmentedControl
            variant="track"
            value={keyMilestonesOnly ? 'key' : 'all'}
            onChange={(value) => onChange(value === 'key')}
            options={[
                { value: 'key', label: $t('Key milestones') },
                { value: 'all', label: $t('All milestones') },
            ]}
        />
    );
}

function MilestoneWindowSelect({ windowDays, onChange }) {
    return (
        <>
            <label htmlFor="key-milestone-window" className="tw-mb-0 tw-text-xs tw-text-charcoal-50">
                {$t('Next')}
            </label>
            <select
                id="key-milestone-window"
                value={windowDays}
                onChange={onChange}
                className="tw-h-8 tw-ps-3 tw-pe-8 tw-py-0 tw-rounded-full tw-border-2 tw-border-charcoal-25 tw-bg-transparent tw-text-xs tw-font-medium tw-text-charcoal focus:tw-ring-0 focus:tw-border-charcoal-25"
            >
                {WINDOW_OPTIONS.map((days) => (
                    <option key={`window-${days}`} value={days}>
                        {$t('{{n}} days', { n: days })}
                    </option>
                ))}
            </select>
        </>
    );
}

export default function ProjectDashboardKeyMilestones(props) {
    const [showAll, setShowAll] = useState(false);

    const milestones = props.data || [];
    const shown = showAll ? milestones : milestones.slice(0, TOP_N);
    const showToggle = milestones.length > TOP_N;
    const windowDays = props.windowDays ?? DEFAULT_WINDOW_DAYS;
    const keyMilestonesOnly = props.keyMilestonesOnly !== false;

    // The panel has no lower bound, so it can hold rows the "key milestones due" tile
    // above never counts. The caption only earns its space once that has actually
    // happened — i.e. once there is a Late row on screen.
    const hasLateRows = milestones.some((row) => row.rag_status === 'late');

    const openMilestone = (activityId) => {
        window.location.href = props.viewProjectActivityDetailsUrl.replace('ACTIVITY_ID', activityId);
    };

    return (
        <div className="table__card">
            <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <h4 className="table__header tw-mb-0">
                    {keyMilestonesOnly ? $t('Key milestones — next {{n}} days', { n: windowDays }) : $t('Milestones — next {{n}} days', { n: windowDays })}
                </h4>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <MilestoneScopeToggle keyMilestonesOnly={keyMilestonesOnly} onChange={props.changeKeyMilestonesOnly} />
                    <MilestoneWindowSelect windowDays={windowDays} onChange={props.changeWindowDays} />
                </div>
            </div>

            {props.loading ? (
                <LoadingSpinner />
            ) : milestones.length === 0 ? (
                <ProjectDashboardEmptyState
                    headline={
                        keyMilestonesOnly
                            ? $t('No key milestones due in the next {{n}} days', { n: windowDays })
                            : $t('No milestones due in the next {{n}} days', { n: windowDays })
                    }
                    supporting={$t('Mark an activity as a key milestone to track it here, with its functional area, due date and delivery status')}
                    canManage={props.canManage}
                    onAddActivity={props.onAddActivity}
                    importProjectPlanUrl={props.importProjectPlanUrl}
                />
            ) : (
                <>
                    {/* Deliberately NOT `.inbud-table`. That shared class paints a blue header,
                        #f1f1f1 rows and vertical grid lines; the wireframe for this card specifies
                        a light-grey header, white rows and horizontal rules only, so the styling is
                        set here instead. Auto layout, so the three explicit widths are hints the
                        content can push back on rather than mandates — measured, Milestone is the
                        widest column at every width this dashboard is used at (652px at 1200,
                        300px at 768). */}
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-border-collapse">
                            <thead>
                                <tr className="tw-bg-charcoal-10">
                                    <th className="tw-py-3 tw-pe-4 tw-ps-3 tw-text-start tw-text-sm tw-font-semibold tw-text-charcoal-75">{$t('Milestone')}</th>
                                    <th className="tw-w-48 tw-py-3 tw-pe-4 tw-text-start tw-text-sm tw-font-semibold tw-text-charcoal-75">
                                        {$t('Functional area')}
                                    </th>
                                    <th className="tw-w-32 tw-py-3 tw-pe-4 tw-text-start tw-text-sm tw-font-semibold tw-text-charcoal-75">{$t('Due')}</th>
                                    <th className="tw-w-24 tw-py-3 tw-text-start tw-text-sm tw-font-semibold tw-text-charcoal-75">{$t('Status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((row) => (
                                    <MilestoneRow key={`key-milestone-${row.id}`} row={row} onOpen={openMilestone} />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {showToggle && (
                        <div className="tw-mt-4 tw-text-xs tw-text-charcoal-50">
                            <span>
                                {showAll
                                    ? $t('Showing all {{m}} milestones', { m: milestones.length })
                                    : $t('Showing top {{n}} of {{m}} milestones', { n: shown.length, m: milestones.length })}
                            </span>
                            <span> · </span>
                            <button type="button" className="tw-text-blue tw-font-medium focus:tw-outline-none" onClick={() => setShowAll((prev) => !prev)}>
                                {showAll ? $t('Show less') : $t('Show all ({{count}})', { count: milestones.length })}
                            </button>
                        </div>
                    )}

                    {hasLateRows && (
                        <div className="tw-mt-2 tw-text-xs tw-text-charcoal-50">
                            {$t(
                                'Key milestones that are already overdue are listed here too, so this panel can hold more rows than the "Key milestones due ({{n}} days)" figure above',
                                {
                                    n: windowDays,
                                },
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
